import { useState, useCallback } from 'react';
import { api } from '../lib/api';

export const CONSENT_KEY = 'gdpr_consent';
const POLICY_VERSION = '1.0';

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(functional) {
  const record = {
    essential: true,
    functional,
    analytics: false,
    version: POLICY_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  return record;
}

async function postConsent(functional) {
  try {
    await api.post('/students/gdpr/consent/', {
      functional,
      analytics: false,
      policy_version: POLICY_VERSION,
    });
  } catch {
    // Non-blocking — consent stored locally regardless
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasConsented = consent !== null;
  const functional = consent?.functional ?? false;

  const acceptAll = useCallback(async () => {
    const record = writeConsent(true);
    setConsent(record);
    await postConsent(true);
  }, []);

  const rejectNonEssential = useCallback(async () => {
    const record = writeConsent(false);
    setConsent(record);
    await postConsent(false);
  }, []);

  const savePreferences = useCallback(async (functionalChoice) => {
    const record = writeConsent(functionalChoice);
    setConsent(record);
    setSettingsOpen(false);
    await postConsent(functionalChoice);
  }, []);

  return {
    hasConsented,
    functional,
    consent,
    settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    acceptAll,
    rejectNonEssential,
    savePreferences,
  };
}

/** Synchronous check for use outside React (e.g. in localStorage read/write helpers). */
export function canUseStorage() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.version === POLICY_VERSION && parsed?.functional === true;
  } catch {
    return false;
  }
}
